import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Linkedin, Clock, MessageSquare } from 'lucide-react';
import './Contact.css';

const Contact = () => {
  const [formState, setFormState] = useState({ name: '', email: '', company: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { name, email, company, message } = formState;
    const subject = encodeURIComponent(`Mixality inquiry from ${name}${company ? ' at ' + company : ''}`);
    const body = encodeURIComponent(`Hi Richard,\n\n${message}\n\nFrom: ${name}\nEmail: ${email}${company ? '\nCompany: ' + company : ''}`);
    window.location.href = `mailto:mrichard.hudson@mixality.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  const channels = [
    {
      icon: <Mail size={22} />,
      label: 'Email',
      value: 'mrichard.hudson@mixality.com',
      href: 'mailto:mrichard.hudson@mixality.com',
    },
    {
      icon: <Linkedin size={22} />,
      label: 'LinkedIn',
      value: 'linkedin.com/company/mixality',
      href: 'https://linkedin.com/company/mixality',
    },
    {
      icon: <Clock size={22} />,
      label: 'Response Time',
      value: 'Usually within 1 business day',
      href: null,
    },
  ];

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="container">
          <motion.div
            className="contact-hero-inner"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">Contact</span>
            <h1>Let's Talk About Your QA</h1>
            <p className="contact-lead">
              Book a free 20-minute discovery call, or send a message and I'll
              get back to you within one business day.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="contact-body">
        <div className="container">
          <div className="contact-grid">

            <motion.div
              className="contact-form-col"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="contact-card">
                <div className="contact-card-header">
                  <MessageSquare size={20} />
                  <span>Send a Message</span>
                </div>

                {submitted ? (
                  <div className="form-success">
                    <span className="success-icon">✓</span>
                    <p>Your email client should have opened. If not, email me directly at <a href="mailto:mrichard.hudson@mixality.com">mrichard.hudson@mixality.com</a>.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="contact-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name">Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          placeholder="Your name"
                          value={formState.name}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          placeholder="you@company.com"
                          value={formState.email}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="company">Company</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        placeholder="Your company (optional)"
                        value={formState.company}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="message">Message *</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        placeholder="Tell me a bit about your situation — what you're building, what's broken, or what you'd like to improve."
                        value={formState.message}
                        onChange={handleChange}
                      />
                    </div>
                    <button type="submit" className="btn btn-primary contact-submit">
                      Send Message
                    </button>
                  </form>
                )}
              </div>
            </motion.div>

            <motion.div
              className="contact-info-col"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="contact-direct">
                <h3>Prefer to reach out directly?</h3>
                <div className="contact-channels">
                  {channels.map((c, i) => (
                    <div key={i} className="contact-channel">
                      <div className="channel-icon">{c.icon}</div>
                      <div className="channel-info">
                        <span className="channel-label">{c.label}</span>
                        {c.href ? (
                          <a href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="channel-value">
                            {c.value}
                          </a>
                        ) : (
                          <span className="channel-value">{c.value}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="contact-discovery">
                <h3>What to expect on a discovery call</h3>
                <ul className="discovery-list">
                  <li>20 minutes, no pitch, no pressure</li>
                  <li>I'll ask about your stack and current testing setup</li>
                  <li>You'll know by the end whether I can help and how</li>
                  <li>If I'm not the right fit, I'll tell you</li>
                </ul>
                <a
                  href="mailto:mrichard.hudson@mixality.com?subject=Free Discovery Call"
                  className="btn btn-primary contact-book-btn"
                >
                  Book a Free Discovery Call
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
